// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled, Icon, PreventPropagation} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {PopoverType, useHover, usePopover, useResponsive} from '@supermove/hooks';
import {ProjectModel, TagModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// Shared
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import EditProjectTagsPopover from '@shared/modules/Project/components/EditProjectTagsPopover';

const Container = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const DiscreetButton = Styled.ButtonV2<{isActive: boolean; isHovered: boolean; hasTags: boolean}>`
  background-color: ${({isActive, isHovered}) => (isActive ? colors.white : isHovered ? colors.gray.background : 'transparent')};
  border-width: 1px;
  border-radius: 4px;
  border-color: ${({isActive, isHovered}) => (isActive ? colors.blue.hover : isHovered ? colors.gray.border : 'transparent')};
  padding-horizontal: 4px;
  padding-vertical: ${({hasTags}) => (hasTags ? 2 : 4)}px;
  margin-left: -6px;
  margin-top: ${({hasTags}) => (hasTags ? -4 : -2)}px;
  margin-bottom: ${({hasTags}) => (hasTags ? -8 : -6)}px;
`;

const EmojiContainer = Styled.View`
  background-color: ${colors.gray.background};
  height: 24px;
  width: 24px;
  border-radius: 12px;
  border-width: 1px;
  border-color: ${colors.gray.border};
  align-items: center;
  justify-content: center;
`;

const getHoverText = ({tags}: {tags: TagModel[]}) => {
  return tags.map((tag) => `${tag.emoji} ${tag.name}`).join('\n');
};

const getIsLastEmoji = ({
  tags,
  index,
  numberOfVisibleSelections,
}: {
  tags: TagModel[];
  index: number;
  numberOfVisibleSelections: number;
}) => {
  return tags.length <= numberOfVisibleSelections
    ? index === tags.length - 1
    : index === numberOfVisibleSelections - 1;
};

const DesktopTags = ({
  tags,
  numberOfVisibleSelections,
  upsertProjectTagPopover,
}: {
  tags: TagModel[];
  upsertProjectTagPopover: PopoverType;
  numberOfVisibleSelections: number;
}) => {
  const {ref, isHovered} = useHover();
  const responsive = useResponsive();
  const hasTags = _.some(tags);
  const isActive = upsertProjectTagPopover.isOpen;

  return (
    <DiscreetButton
      ref={ref}
      onPress={upsertProjectTagPopover.handleToggle}
      isHovered={isHovered && responsive.desktop}
      isActive={isActive}
      hasTags={hasTags}
    >
      {hasTags ? (
        <TextTooltip text={hasTags ? getHoverText({tags}) : ''}>
          <Row>
            {tags.map((tag, index) => {
              if (index >= numberOfVisibleSelections) {
                return null;
              }

              const isLast = getIsLastEmoji({tags, index, numberOfVisibleSelections});

              return (
                <EmojiContainer key={index} style={{marginRight: isLast ? 0 : -8}}>
                  <Text.Body>{tag.emoji}</Text.Body>
                </EmojiContainer>
              );
            })}
            {tags.length > numberOfVisibleSelections && (
              <React.Fragment>
                <Space width={4} />
                <Text.Body
                  style={{
                    color: isActive
                      ? colors.gray.secondary
                      : isHovered
                        ? colors.blue.hover
                        : colors.gray.secondary,
                  }}
                >
                  {`+${tags.length - numberOfVisibleSelections}`}
                </Text.Body>
              </React.Fragment>
            )}
          </Row>
        </TextTooltip>
      ) : (
        <Icon
          source={Icon.Plus}
          color={isHovered ? colors.blue.hover : colors.blue.interactive}
          size={16}
        />
      )}
    </DiscreetButton>
  );
};

const MobileTags = ({
  tags,
  upsertProjectTagPopover,
}: {
  tags: TagModel[];
  upsertProjectTagPopover: PopoverType;
}) => {
  return (
    <Container>
      {tags.map((tag, index) => {
        return (
          <React.Fragment key={tag.id}>
            <Row>
              <EmojiContainer key={index}>
                <Text.Body>{tag.emoji}</Text.Body>
              </EmojiContainer>
              <Space width={8} />
              <Text.Body>{tag.name}</Text.Body>
            </Row>
            <Space height={4} />
          </React.Fragment>
        );
      })}
      <Space height={4} />
      <TertiaryButton onPress={upsertProjectTagPopover.handleOpen}>
        <Icon source={Icon.Plus} color={colors.blue.interactive} size={16} />
        <Space width={8} />
        <Text.Label style={{color: colors.blue.interactive}}>Add Tag</Text.Label>
      </TertiaryButton>
      <Space height={8} />
    </Container>
  );
};

const ProjectTagsButton = ({
  project,
  numberOfVisibleSelections = 4,
}: {
  project: ProjectModel;
  numberOfVisibleSelections?: number;
}) => {
  const upsertProjectTagPopover = usePopover({
    name: 'Upsert Project Tag Modal',
    enableTracking: true,
  });
  const responsive = useResponsive();
  const tags = project.tagAssignmentsByProject.map((tagAssignment) => tagAssignment.tag);

  return (
    <PreventPropagation>
      <EditProjectTagsPopover
        key={upsertProjectTagPopover.key}
        tagIds={tags.map((tag) => tag.id)}
        projectId={project.id}
        projectUuid={project.uuid}
        popover={upsertProjectTagPopover}
      >
        {responsive.desktop ? (
          <DesktopTags
            tags={tags}
            numberOfVisibleSelections={numberOfVisibleSelections}
            upsertProjectTagPopover={upsertProjectTagPopover}
          />
        ) : (
          <MobileTags tags={tags} upsertProjectTagPopover={upsertProjectTagPopover} />
        )}
      </EditProjectTagsPopover>
    </PreventPropagation>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectTagsButton.fragment = gql`
  fragment ProjectTagsButton on Project {
    id
    uuid
    tagAssignmentsByProject {
      id
      tag {
        id
        name
        emoji
      }
    }
  }
`;

export default ProjectTagsButton;
